import React, { useEffect, useState } from "react";
import { InputNumber, Alert, Form, Table, Button, Space, Modal, Input, message, Row, Col, Tabs, Select, Tag } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, MinusOutlined, HistoryOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { DatePicker } from "antd";
import {
    getEtats,
    createEtat,
    updateEtat,
    deleteEtat,
    getArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    getCategories,
    createCategorie,
    updateCategorie,
    deleteCategorie,
    getEmplacement,
    updateEmplacement,
    createEmplacement,
    createMouvement,
    getMouvementsStock
} from "../../../api/inventaire";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import formatDateFr, { getPermissionsFromToken, hasPermission } from "../../../utils/utils";


const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;
const Inventaire = () => {
    // -------------------- Articles --------------------
    const [articles, setArticles] = useState([]);
    const [loadingArticles, setLoadingArticles] = useState(false);
    const [modalArticleVisible, setModalArticleVisible] = useState(false);
    const [isEditingArticle, setIsEditingArticle] = useState(false);
    const [currentArticle, setCurrentArticle] = useState(null);
    const [formArticle, setFormArticle] = useState({
        nom: "", description: "", quantite: 0,
        categorie_id: "", etat_id: "", emplacement_id: ""
    });
    const [displayedMouvements, setDisplayedMouvements] = useState([]);


    // -------------------- États --------------------
    const [etats, setEtats] = useState([]);
    const [modalEtatVisible, setModalEtatVisible] = useState(false);
    const [currentEtat, setCurrentEtat] = useState(null);
    const [formEtat, setFormEtat] = useState({ libelle: "" });

    // -------------------- Catégories --------------------
    const [categories, setCategories] = useState([]);
    const [modalCategorieVisible, setModalCategorieVisible] = useState(false);
    const [currentCategorie, setCurrentCategorie] = useState(null);
    const [formCategorie, setFormCategorie] = useState({ nom: "", description: "" });

    // -------------------- Emplacement --------------------
    const [emplacement, setEmplacement] = useState([]);
    const [modalEmplacementVisible, setModalEmplacementVisible] = useState(false);
    const [currentEmplacement, setCurrentEmplacement] = useState(null);
    const [formEmplacement, setFormEmplacement] = useState({ nom: "", description: "" });


    // MOUVEMENT
    const [showMouvementModal, setShowMouvementModal] = useState(false);
    const [typeMouvement, setTypeMouvement] = useState(null);
    const [form] = Form.useForm()

    const [mouvements, setMouvements] = useState([]);
    const [loadingMouvements, setLoadingMouvements] = useState(false);
    // -------------------- Fetch data --------------------
    const fetchAll = async () => {
        await fetchMouvementList()
        await fetchArticlesList();
        await fetchEtatsList();
        await fetchCategoriesList();
        await fetchEmplacementList();
    };

    const stringToColor = (str) => {
        const colors = [
            "blue", "green", "orange", "purple", "cyan",
            "magenta", "geekblue", "volcano", "gold", "lime"
        ];

        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const getEtatColor = (libelle) => {
        if (!libelle) return "default";

        const value = libelle.toLowerCase();

        if (value.includes("mauvais") || value.includes("cassé")) return "red";
        if (value.includes("neuf")) return "green";
        if (value.includes("usé")) return "orange";

        return stringToColor(libelle);
    };

    const categorieFilters = categories.map((c) => ({
        text: c.nom,
        value: c.id
    }));

    const etatFilters = etats.map((e) => ({
        text: e.libelle,
        value: e.id
    }));

    const emplacementFilters = emplacement.map((e) => ({
        text: e.nom,
        value: e.id
    }));


    const mouvementTypeFilters = [
        { text: "Entrée", value: "ENTREE" },
        { text: "Sortie", value: "SORTIE" }
    ];

    const articleFilters = articles.map(a => ({
        text: a.nom,
        value: a.id
    }));

    const emplacementMvtFilters = emplacement.map(e => ({
        text: e.nom,
        value: e.id
    }));

    const etatMvtFilters = etats.map(e => ({
        text: e.libelle,
        value: e.id
    }));

    const fetchArticlesList = async () => {
        setLoadingArticles(true);
        const res = await getArticles();
        setArticles(res.data || []);
        setLoadingArticles(false);
    };

    const fetchMouvementList = async () => {
        setLoadingMouvements(true);
        const res = await getMouvementsStock();
        setMouvements(res.data || []);
        setLoadingMouvements(false);
    };


    const fetchEtatsList = async () => {
        try {
            const res = await getEtats();
            if (res.status === "success") setEtats(res.data || []);
        } catch { message.error("Erreur chargement états"); }
    };

    const fetchCategoriesList = async () => {
        try {
            const res = await getCategories();
            if (res.status === "success") setCategories(res.data || []);
        } catch { message.error("Erreur chargement catégories"); }
    };

    const fetchEmplacementList = async () => {
        try {
            const res = await getEmplacement();
            if (res.status === "success") setEmplacement(res.data || []);
        } catch { message.error("Erreur chargement emplacement"); }
    };

    // -------------------- Lifecycle --------------------
    React.useEffect(() => { fetchAll(); }, []);

    // -------------------- Handlers Add / Update --------------------
    const handleSaveArticle = async () => {
        try {
            const apiFunc = isEditingArticle ? updateArticle : createArticle;
            const payload = isEditingArticle ? { id: currentArticle.id, ...formArticle } : formArticle;
            const res = await apiFunc(payload);
            if (res.status === "success") {
                message.success(isEditingArticle ? "Article mis à jour" : "Article ajouté");
                setModalArticleVisible(false);
                fetchArticlesList();
            } else message.error(res.description || "Erreur serveur");
        } catch { message.error("Erreur serveur"); }
    };

    const handleSaveEtat = async () => {
        try {
            const apiFunc = currentEtat ? updateEtat : createEtat;
            const payload = currentEtat ? { id: currentEtat.id, ...formEtat } : formEtat;
            const res = await apiFunc(payload);
            if (res.status === "success") {
                message.success(currentEtat ? "État modifié" : "État ajouté");
                setModalEtatVisible(false);
                setFormEtat({ libelle: "" });
                setCurrentEtat(null);
                fetchEtatsList();
            } else message.error(res.description || "Erreur serveur");
        } catch { message.error("Erreur serveur"); }
    };

    const handleSaveCategorie = async () => {
        try {
            const apiFunc = currentCategorie ? updateCategorie : createCategorie;
            const payload = currentCategorie ? { id: currentCategorie.id, ...formCategorie } : formCategorie;
            const res = await apiFunc(payload);
            if (res.status === "success") {
                message.success(currentCategorie ? "Catégorie modifiée" : "Catégorie ajoutée");
                setModalCategorieVisible(false);
                setFormCategorie({ nom: "", description: "" });
                setCurrentCategorie(null);
                fetchCategoriesList();
            } else message.error(res.description || "Erreur serveur");
        } catch { message.error("Erreur serveur"); }
    };

    const handleSaveEmplacement = async () => {
        try {
            const apiFunc = currentEmplacement ? updateEmplacement : createEmplacement;
            const payload = currentEmplacement ? { id: currentEmplacement.id, ...formEmplacement } : formEmplacement;
            const res = await apiFunc(payload);
            if (res.status === "success") {
                message.success(currentCategorie ? "Emplacement modifiée" : "Emplacement ajoutée");
                setModalEmplacementVisible(false);
                setFormEmplacement({ nom: "", description: "" });
                setCurrentEmplacement(null);
                fetchEmplacementList();
            } else message.error(res.description || "Erreur serveur");
        } catch { message.error("Erreur serveur"); }
    };

    // -------------------- Delete handlers --------------------
    const handleDeleteArticle = async (id) => {
        try {
            const res = await deleteArticle(id);
            if (res.status === "success") message.success("Article supprimé");
            else message.error(res.description || "Échec suppression");
            fetchArticlesList();
        } catch { message.error("Erreur serveur"); }
    };

    const handleDeleteEtat = async (id) => {
        try {
            const res = await deleteEtat(id);
            if (res.status === "success") message.success("État supprimé");
            else message.error(res.description || "Échec suppression");
            fetchEtatsList();
        } catch { message.error("Erreur serveur"); }
    };

    const handleDeleteCategorie = async (id) => {
        try {
            const res = await deleteCategorie(id);
            if (res.status === "success") message.success("Catégorie supprimée");
            else message.error(res.description || "Échec suppression");
            fetchCategoriesList();
        } catch { message.error("Erreur serveur"); }
    };

    const getCategorieName = (id) => {
        const cat = categories.find((c) => c.id === id);
        return cat ? cat.nom : "-";
    };

    const getEtatName = (id) => {
        const etat = etats.find((e) => e.id === id);
        return etat ? etat.libelle : "-";
    };

    const getEmplacementName = (id) => {
        const emp = emplacement.find((e) => e.id === id);
        return emp ? emp.nom : "-";
    };

    // -------------------- Table columns --------------------
    const columnsArticles = [
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
            filters: articles.map((a) => ({ text: a.code, value: a.code })),
            onFilter: (value, record) => record.code === value
        },
        {
            title: "Nom",
            dataIndex: "nom",
            key: "nom",
            align: "center",
            filters: articles.map((a) => ({ text: a.nom, value: a.nom })),
            onFilter: (value, record) => record.nom === value
        },
        {
            title: "Stock",
            dataIndex: "quantite",
            render: (q) => (
                <Tag color={q === 0 ? "red" : q < 5 ? "orange" : "green"}>
                    {q}
                </Tag>
            ),

        },
        {
            title: "Catégorie",
            dataIndex: "categorie_id",
            key: "categorie",
            filters: categorieFilters,
            onFilter: (value, record) => record.categorie_id === value,
            render: (id) => {
                const nom = getCategorieName(id);
                return <Tag color={stringToColor(nom)}>{nom}</Tag>;
            }
        },
        {
            title: "État",
            dataIndex: "etat_id",
            key: "etat",
            filters: etatFilters,
            align: "center",
            onFilter: (value, record) => record.etat_id === value,
            render: (id) => {
                const libelle = getEtatName(id);
                return <Tag color={getEtatColor(libelle)}>{libelle}</Tag>;
            }
        },
        {
            title: "Emplacement",
            dataIndex: "emplacement_id",
            key: "emplacement",
            align: "center",
            filters: emplacementFilters,
            onFilter: (value, record) => record.emplacement_id === value,
            render: (id) => {
                const nom = getEmplacementName(id);
                return <Tag color={stringToColor(nom)}>{nom}</Tag>;
            }
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (_, record) => (
                <Space>
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ARTICLE_EDIT") && <Button
                            icon={<EditOutlined />}
                            onClick={() => {
                                setIsEditingArticle(true);
                                setCurrentArticle(record);
                                setFormArticle(record);
                                setModalArticleVisible(true);
                            }}
                        />
                    }

                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ARTICLE_DELETE") && <Button
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteArticle(record.id)}
                        />
                    }


                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ARTICLE_ADD_ENTREE") && <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setCurrentArticle(record);
                                setTypeMouvement("ENTREE");
                                setShowMouvementModal(true);
                            }}
                        >
                            Entrée
                        </Button>
                    }

                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ARTICLE_ADD_SORTIE") && <Button
                            danger
                            icon={<MinusOutlined />}
                            onClick={() => {
                                setCurrentArticle(record);
                                setTypeMouvement("SORTIE");
                                setShowMouvementModal(true);
                            }}
                        >
                            Sortie
                        </Button>
                    }

                </Space>
            )
        }
    ];

    const columnsEtats = [
        { title: "Libellé", dataIndex: "libelle", key: "libelle" },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ETAT_EDIT") && <Button icon={<EditOutlined />} onClick={() => {
                            setCurrentEtat(record);
                            setFormEtat({ libelle: record.libelle });
                            setModalEtatVisible(true);
                        }} />
                    }
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_ETAT_DELETE") && <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteEtat(record.id)} />
                    }


                </Space>
            )
        }
    ];

    const columnsCategories = [
        { title: "Nom", dataIndex: "nom", key: "nom" },
        { title: "Description", dataIndex: "description", key: "description" },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_CATEGORIE_EDIT") && <Button icon={<EditOutlined />} onClick={() => {
                            setCurrentCategorie(record);
                            setFormCategorie({ nom: record.nom, description: record.description });
                            setModalCategorieVisible(true);
                        }} />
                    }

                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_CATEGORIE_DELETE") && <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteCategorie(record.id)} />
                    }


                </Space>
            )
        }
    ];

    const columnsEmplacement = [
        { title: "Nom", dataIndex: "nom", key: "nom" },
        { title: "Description", dataIndex: "description", key: "description" },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_EMPLACEMENT_EDIT") && <Button icon={<EditOutlined />} onClick={() => {
                            setCurrentEmplacement(record);
                            setFormEmplacement({ nom: record.nom, description: record.description });
                            setModalEmplacementVisible(true);
                        }} />
                    }
                    {
                        hasPermission(getPermissionsFromToken(), "INVENTAIRE_EMPLACEMENT_DELETE") && <Button danger icon={<DeleteOutlined />} onClick={() => handleDeleteCategorie(record.id)} />
                    }


                </Space>
            )
        }
    ];

    const columnsMouvements = [
        {
            title: "Date",
            dataIndex: "create_at",
            key: "create_at",
            sorter: (a, b) => new Date(a.create_at) - new Date(b.create_at),
            render: (date) => dayjs(date).format("DD/MM/YYYY HH:mm"),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <RangePicker
                        showTime
                        value={selectedKeys[0]}
                        onChange={(dates) => {
                            setSelectedKeys(dates ? [dates] : []);
                        }}
                        style={{ marginBottom: 8, width: "100%" }}
                        format="DD/MM/YYYY HH:mm"
                    />

                    <Space>
                        <Button
                            type="primary"
                            size="small"
                            onClick={confirm}
                        >
                            OK
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                clearFilters();
                                confirm();
                            }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, record) => {
                if (!value || value.length !== 2) return true;

                const [start, end] = value;
                const recordDate = dayjs(record.create_at);

                return (
                    recordDate.isAfter(start.startOf("minute")) &&
                    recordDate.isBefore(end.endOf("minute"))
                );
            }
        },

        {
            title: "Type",
            dataIndex: "type_mouvement",
            key: "type_mouvement",
            filters: mouvementTypeFilters,
            onFilter: (value, record) => record.type_mouvement === value,
            align: "center",
            render: (type) => (
                <Tag color={type === "ENTREE" ? "green" : "red"}>
                    {type}
                </Tag>
            )
        },
        {
            title: "Article",
            dataIndex: "article_id",
            key: "article_id",
            filters: articleFilters,
            onFilter: (value, record) => record.article_id === value,
            render: (id) => {
                const art = articles.find(a => a.id === id);
                return art ? art.nom : "-";
            }
        },
        {
            title: "Quantité",
            dataIndex: "quantite",
            key: "quantite",
            sorter: (a, b) => a.quantite - b.quantite,
            align: "center",
            render: (q, record) => (
                <Tag color={record.type_mouvement === "ENTREE" ? "green" : "red"}>
                    {record.type_mouvement === "ENTREE" ? "+" : "-"}{q}
                </Tag>
            )
        },
        {
            title: "Emplacement",
            dataIndex: "emplacement_id",
            key: "emplacement_id",
            filters: emplacementMvtFilters,
            onFilter: (value, record) => record.emplacement_id === value,
            render: (id) => getEmplacementName(id)
        },
        {
            title: "État",
            dataIndex: "etat_id",
            key: "etat_id",
            filters: etatMvtFilters,
            onFilter: (value, record) => record.etat_id === value,
            render: (id) => {
                const libelle = getEtatName(id);
                return <Tag color={getEtatColor(libelle)}>{libelle}</Tag>;
            }
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            onFilter: (value, record) =>
                record.description?.toLowerCase().includes(value.toLowerCase()),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="Rechercher"
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={confirm}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button type="primary" onClick={confirm} size="small">
                            OK
                        </Button>
                        <Button onClick={clearFilters} size="small">
                            Reset
                        </Button>
                    </Space>
                </div>
            )
        }
    ];


    const submitMouvement = async (values) => {
        console.log(values);
        const payload = {
            article_id: currentArticle.id,
            type_mouvement: typeMouvement,          // "ENTREE" ou "SORTIE"
            quantite: values.quantite,              // récupérée du Form
            description: values.description,        // récupérée du Form
            user_id: localStorage.getItem("user_id"),
            emplacement_id: values.emplacement_id,
            etat_id: values.etat_id
        };
        const res = await createMouvement(payload)
        if (res.status === "success") {
            message.success(res.description);
        } else {
            message.error(res.description)
        }
        setTimeout(() => {
            setShowMouvementModal(false);
            form.resetFields();   // on vide le form
            fetchArticlesList();
        }, 1200);
    };

    const exportMouvements = () => {
        const dataToExport = (displayedMouvements.length ? displayedMouvements : mouvements)
            .map(m => ({
                Date: new Date(m.create_at).toLocaleString(),
                Type: m.type_mouvement,
                Article: articles.find(a => a.id === m.article_id)?.nom || "",
                Quantité: m.type_mouvement === "ENTREE" ? `+${m.quantite}` : `-${m.quantite}`,
                Emplacement: getEmplacementName(m.emplacement_id),
                État: getEtatName(m.etat_id),
                Description: m.description || ""
            }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Mouvements");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([excelBuffer], {
            type: "application/octet-stream"
        });

        saveAs(blob, `mouvements_stock_${Date.now()}.xlsx`);
    };

    return (
        <div>
            <Row>
                <Col span={24} >
                    <Tabs defaultActiveKey="mouvements" type="card" style={{ height: '77vh', overflowY: 'scroll' }}>

                        {/* -------------------- MOUVEMENT -------------------- */}
                        <TabPane tab="Mouvements" key="mouvements">
                            <Space style={{ marginBottom: 16 }}>
                                {
                                    hasPermission(getPermissionsFromToken(), "INVENTAIRE_MOUVEMENT_EXPORT") && <Button
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        onClick={exportMouvements}
                                    >
                                        Exporter Excel
                                    </Button>
                                }

                            </Space>
                            <Table
                                rowKey="id"
                                columns={columnsMouvements}
                                dataSource={mouvements}
                                loading={loadingMouvements}
                                onChange={(pagination, filters, sorter, extra) => {
                                    setDisplayedMouvements(extra.currentDataSource);
                                }}
                                pagination={{
                                    pageSize: 10,
                                    position: ["bottomCenter"],
                                    className: "custom-pagination",
                                    style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: 12
                                    },
                                    itemRender: (page, type, originalElement) => {
                                        if (type === "page") {
                                            return (
                                                <div
                                                    style={{
                                                        borderRadius: "50%",
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: "1px solid #d9d9d9"
                                                    }}
                                                >
                                                    {page}
                                                </div>
                                            );
                                        }
                                        return originalElement;
                                    }
                                }}
                            />

                        </TabPane>


                        {/* -------------------- Articles -------------------- */}
                        <TabPane tab="Articles" key="articles">
                            {
                                hasPermission(getPermissionsFromToken(), "INVENTAIRE_ARTICLE_ADD") && <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        setIsEditingArticle(false);
                                        setCurrentArticle(null);
                                        setFormArticle({
                                            nom: "",
                                            description: "",
                                            quantite: 0,
                                            categorie_id: "",
                                            etat_id: "",
                                            emplacement_id: ""
                                        });
                                        setModalArticleVisible(true);
                                    }}
                                >
                                    Ajouter
                                </Button>
                            }


                            <Table
                                rowKey="id"
                                columns={columnsArticles}
                                dataSource={articles}
                                loading={loadingArticles}
                                style={{ marginTop: 8 }}
                                pagination={{
                                    pageSize: 10,
                                    position: ["bottomCenter"],
                                    className: "custom-pagination",
                                    style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: 12
                                    },
                                    itemRender: (page, type, originalElement) => {
                                        if (type === "page") {
                                            return (
                                                <div
                                                    style={{
                                                        borderRadius: "50%",
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: "1px solid #d9d9d9"
                                                    }}
                                                >
                                                    {page}
                                                </div>
                                            );
                                        }
                                        return originalElement;
                                    }
                                }}
                            />
                        </TabPane>

                        {/* -------------------- États -------------------- */}
                        <TabPane tab="États" key="etats">
                            {
                                hasPermission(getPermissionsFromToken(), "INVENTAIRE_ETAT_ADD") && <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        setFormEtat({ libelle: "" });
                                        setCurrentEtat(null);
                                        setModalEtatVisible(true);
                                    }}
                                >
                                    Ajouter
                                </Button>
                            }


                            <Table
                                rowKey="id"
                                columns={columnsEtats}
                                dataSource={etats}
                                style={{ marginTop: 8 }}
                                pagination={{
                                    pageSize: 10,
                                    position: ["bottomCenter"],
                                    className: "custom-pagination",
                                    style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: 12
                                    },
                                    itemRender: (page, type, originalElement) => {
                                        if (type === "page") {
                                            return (
                                                <div
                                                    style={{
                                                        borderRadius: "50%",
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: "1px solid #d9d9d9"
                                                    }}
                                                >
                                                    {page}
                                                </div>
                                            );
                                        }
                                        return originalElement;
                                    }
                                }}
                            />
                        </TabPane>

                        {/* -------------------- Catégories -------------------- */}
                        <TabPane tab="Catégories" key="categories">
                            {
                                hasPermission(getPermissionsFromToken(), "INVENTAIRE_CATEGORIE_ADD") && <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => {
                                        setFormCategorie({ nom: "", description: "" });
                                        setCurrentCategorie(null);
                                        setModalCategorieVisible(true);
                                    }}
                                >
                                    Ajouter
                                </Button>
                            }


                            <Table
                                rowKey="id"
                                columns={columnsCategories}
                                dataSource={categories}
                                style={{ marginTop: 8 }}
                                pagination={{
                                    pageSize: 10,
                                    position: ["bottomCenter"],
                                    className: "custom-pagination",
                                    style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: 12
                                    },
                                    itemRender: (page, type, originalElement) => {
                                        if (type === "page") {
                                            return (
                                                <div
                                                    style={{
                                                        borderRadius: "50%",
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: "1px solid #d9d9d9"
                                                    }}
                                                >
                                                    {page}
                                                </div>
                                            );
                                        }
                                        return originalElement;
                                    }
                                }}
                            />
                        </TabPane>

                        {/* -------------------- Emplacements -------------------- */}
                        <TabPane tab="Emplacement" key="emplacement">
                            {
                                 hasPermission(getPermissionsFromToken(), "INVENTAIRE_EMPLACEMENT_ADD") &&  <Button
                                type="primary"
                                icon={<HomeOutlined />}
                                onClick={() => {
                                    setFormEmplacement({ nom: "", description: "" });
                                    // setEmplacement(null);
                                    setModalEmplacementVisible(true);
                                }}
                            >
                                Ajouter
                            </Button>
                            }
                           

                            <Table
                                rowKey="id"
                                columns={columnsEmplacement}
                                dataSource={emplacement}
                                style={{ marginTop: 8 }}
                                pagination={{
                                    pageSize: 10,
                                    position: ["bottomCenter"],
                                    className: "custom-pagination",
                                    style: {
                                        display: "flex",
                                        justifyContent: "center",
                                        marginTop: 12
                                    },
                                    itemRender: (page, type, originalElement) => {
                                        if (type === "page") {
                                            return (
                                                <div
                                                    style={{
                                                        borderRadius: "50%",
                                                        minWidth: 32,
                                                        height: 32,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        border: "1px solid #d9d9d9"
                                                    }}
                                                >
                                                    {page}
                                                </div>
                                            );
                                        }
                                        return originalElement;
                                    }
                                }}
                            />
                        </TabPane>
                    </Tabs>
                </Col>
            </Row>

            {/* -------------------- Modals -------------------- */}
            {/* Article */}
            <Modal title={isEditingArticle ? "Modifier l'article" : "Ajouter un article"} open={modalArticleVisible} onOk={handleSaveArticle} onCancel={() => setModalArticleVisible(false)} okText="Enregistrer" cancelText="Annuler">
                <Input placeholder="Nom" value={formArticle.nom} onChange={(e) => setFormArticle({ ...formArticle, nom: e.target.value })} style={{ marginBottom: 8 }} />
                <Input placeholder="Description" value={formArticle.description} onChange={(e) => setFormArticle({ ...formArticle, description: e.target.value })} style={{ marginBottom: 8 }} />
                {/* <Input placeholder="Quantité" type="number" value={formArticle.quantite} onChange={(e) => setFormArticle({ ...formArticle, quantite: parseInt(e.target.value) || 0 })} style={{ marginBottom: 8 }} /> */}
                <Select
                    placeholder="Catégorie"
                    value={formArticle.categorie_id || undefined}
                    onChange={(value) =>
                        setFormArticle({ ...formArticle, categorie_id: value })
                    }
                    style={{ width: "100%", marginBottom: 8 }}
                >
                    {categories.map((cat) => (
                        <Option key={cat.id} value={cat.id}>
                            {cat.nom}
                        </Option>
                    ))}
                </Select>

                {/* État */}
                <Select
                    placeholder="État"
                    value={formArticle.etat_id || undefined}
                    onChange={(value) =>
                        setFormArticle({ ...formArticle, etat_id: value })
                    }
                    style={{ width: "100%", marginBottom: 8 }}
                >
                    {etats.map((etat) => (
                        <Option key={etat.id} value={etat.id}>
                            {etat.libelle}
                        </Option>
                    ))}
                </Select>

                {/* Emplacement */}
                <Select
                    placeholder="Emplacement"
                    value={formArticle.emplacement_id || undefined}
                    onChange={(value) =>
                        setFormArticle({ ...formArticle, emplacement_id: value })
                    }
                    style={{ width: "100%", marginBottom: 8 }}
                >
                    {emplacement.map((emp) => (
                        <Option key={emp.id} value={emp.id}>
                            {emp.nom}
                        </Option>
                    ))}
                </Select>
            </Modal>

            {/* État */}
            <Modal title={currentEtat ? "Modifier l'état" : "Ajouter un état"} open={modalEtatVisible} onOk={handleSaveEtat} onCancel={() => setModalEtatVisible(false)} okText="Enregistrer" cancelText="Annuler">
                <Input placeholder="Libellé" value={formEtat.libelle} onChange={(e) => setFormEtat({ ...formEtat, libelle: e.target.value })} />
            </Modal>

            {/* Catégorie */}
            <Modal title={currentCategorie ? "Modifier la catégorie" : "Ajouter une catégorie"} open={modalCategorieVisible} onOk={handleSaveCategorie} onCancel={() => setModalCategorieVisible(false)} okText="Enregistrer" cancelText="Annuler">
                <Input placeholder="Nom" value={formCategorie.nom} onChange={(e) => setFormCategorie({ ...formCategorie, nom: e.target.value })} style={{ marginBottom: 8 }} />
                <Input placeholder="Description" value={formCategorie.description} onChange={(e) => setFormCategorie({ ...formCategorie, description: e.target.value })} />
            </Modal>

            {/* Emplacement */}
            <Modal title={currentEmplacement ? "Modifier l' emplacement" : "Ajouter une emplacement"} open={modalEmplacementVisible} onOk={handleSaveEmplacement} onCancel={() => setModalEmplacementVisible(false)} okText="Enregistrer" cancelText="Annuler">
                <Input placeholder="Nom" value={formEmplacement.nom} onChange={(e) => setFormEmplacement({ ...formEmplacement, nom: e.target.value })} style={{ marginBottom: 8 }} />
                <Input placeholder="Description" value={formEmplacement.description} onChange={(e) => setFormEmplacement({ ...formEmplacement, description: e.target.value })} />
            </Modal>

            <Modal
                open={showMouvementModal}
                title={`${typeMouvement === "ENTREE" ? "Entrée" : "Sortie"} de stock`}
                onCancel={() => setShowMouvementModal(false)}
                onOk={() => form.submit()}
                okText="Valider"
                cancelText="Annuler"
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={submitMouvement}
                >
                    {/* Ligne 1 */}
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="quantite"
                                label="Quantité"
                                rules={[{ required: true }]}
                            >
                                <InputNumber
                                    min={1}
                                    placeholder="Ex : 10"
                                    style={{ width: "100%" }}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="emplacement_id"
                                label="Emplacement"
                                rules={[{ required: true }]}
                            >
                                <Select
                                    placeholder="Sélectionner un emplacement"
                                    style={{ width: "100%" }}
                                    size="large"
                                    showSearch
                                >
                                    {emplacement?.map(e => (
                                        <Select.Option key={e.id} value={e.id}>
                                            {e.nom}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="etat_id"
                                label="État de l’article"
                                rules={[{ required: true }]}
                                showSearch
                            >
                                <Select
                                    placeholder="Sélectionner l’état"
                                    style={{ width: "100%" }}
                                    showSearch
                                    size="large"
                                >
                                    {etats?.map(e => (
                                        <Select.Option key={e.id} value={e.id}>
                                            {e.libelle}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="Motif / Description"
                            >
                                <Input.TextArea
                                    rows={4}
                                    placeholder="Ex : casse, transfert, correction de stock..."
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>


        </div>
    );
};

export default Inventaire;
